'use client';
import React, { useState } from 'react';
import { X, Trash2, BookOpen, FlaskConical, Settings, Eye } from 'lucide-react';
import { ALL_COMPONENTS, COMPONENT_GROUPS } from '@/lib/archsim-data';
import type { CanvasNode } from '@/lib/archsim-data';
import type { SimulationState } from './ArchitectureCanvasScreen';
import Icon from '@/components/ui/AppIcon';


interface NodeInspectorPanelProps {
  node: CanvasNode;
  simState: SimulationState;
  onClose: () => void;
  onUpdateConfig: (id: string, config: Record<string, string | number | boolean>) => void;
  onUpdateLabel: (id: string, label: string) => void;
  onTriggerExperiment: (nodeId: string, experiment: string) => void;
  onDeleteNode: (id: string) => void;
}

type InspectorTab = 'overview' | 'config' | 'learn' | 'experiment';

const LEARN_CONTENT: Record<string, { concept: string; useCase: string; tradeoffs: string; pattern: string }> = {
  'redis': {
    concept: 'Redis is an in-memory data structure store used as a cache, message broker, and database. Its sub-millisecond latency comes from storing all data in RAM.',
    useCase: 'Cache database query results, store session tokens, implement rate limiting counters, pub/sub messaging.',
    tradeoffs: 'Memory is expensive. Data is volatile unless persistence is configured. Single-threaded for most operations.',
    pattern: 'Cache-Aside: Application checks cache first. On miss, fetches from DB, writes to cache with TTL, returns data.',
  },
  'kafka': {
    concept: 'Kafka is a distributed event streaming platform. Producers write to topics (partitioned logs). Consumers read at their own pace. Messages persist for a configurable retention period.',
    useCase: 'Decouple microservices, stream processing pipelines, activity tracking, log aggregation, event sourcing.',
    tradeoffs: 'Complex to operate. At-least-once delivery by default. Consumer groups enable parallel processing.',
    pattern: 'Event Sourcing: Every state change is published as an immutable event. State is reconstructed by replaying events.',
  },
  'load-balancer': {
    concept: 'A load balancer distributes incoming network traffic across multiple backend servers to maximize throughput, minimize latency, and ensure no single server is overwhelmed.',
    useCase: 'Horizontal scaling of stateless services, zero-downtime deployments, health-based routing.',
    tradeoffs: 'Adds one network hop. Session affinity (sticky sessions) conflicts with stateless design. Single point of failure if not redundant.',
    pattern: 'Round Robin, Least Connections, IP Hash, Weighted. Use health checks to remove failed instances automatically.',
  },
  'circuit-breaker': {
    concept: 'The circuit breaker pattern prevents cascading failures. When a downstream service fails repeatedly, the breaker "opens" and fails fast without waiting for timeouts.',
    useCase: 'Protecting against slow downstream services, preventing thread pool exhaustion, enabling graceful degradation.',
    tradeoffs: 'Requires tuning thresholds. Half-open state needs careful handling. Adds complexity to service interactions.',
    pattern: 'Closed → Open (after N failures) → Half-Open (probe after timeout) → Closed (if probe succeeds).',
  },
};

const DEFAULT_LEARN = {
  concept: 'This component represents a key building block in distributed system design. Understanding its characteristics helps you make better architectural decisions.',
  useCase: 'Evaluate when to use this component based on your system\'s latency, throughput, and consistency requirements.',
  tradeoffs: 'Every component introduces trade-offs in complexity, cost, and operational overhead. Consider these carefully.',
  pattern: 'Study how this component interacts with others in common architectural patterns for your domain.',
};

export default function NodeInspectorPanel({ node, simState, onClose, onUpdateConfig, onUpdateLabel, onTriggerExperiment, onDeleteNode }: NodeInspectorPanelProps) {
  const [activeTab, setActiveTab] = useState<InspectorTab>('overview');
  const def = ALL_COMPONENTS.find(c => c.id === node.defId);
  if (!def) return null;

  const groupColor = COMPONENT_GROUPS[def.group].color;
  const learn = LEARN_CONTENT[node.defId] ?? DEFAULT_LEARN;

  const statusBg = node.status === 'active' ? 'bg-positive/10 text-positive border-positive/20' :
    node.status === 'degraded' ? 'bg-warning/10 text-warning border-warning/20' :
    node.status === 'failed'? 'bg-danger/10 text-danger border-danger/20' : 'bg-secondary text-muted-foreground border-border';

  const tabs: { id: InspectorTab; label: string; icon: React.ElementType }[] = [
    { id: 'overview', label: 'Overview', icon: Eye },
    { id: 'config', label: 'Config', icon: Settings },
    { id: 'learn', label: 'Learn', icon: BookOpen },
    { id: 'experiment', label: 'Experiment', icon: FlaskConical },
  ];

  return (
    <div className="w-full md:w-72 shrink-0 flex flex-col rounded-t-xl md:rounded-none bg-card border-l border-border overflow-hidden animate-slide-down">
      {/* Header */}
      <div className="flex items-start justify-between px-4 py-3 border-b border-border">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className="text-lg">{def.icon}</span>
            <span className="text-sm font-semibold text-foreground truncate">{node.label}</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs px-1.5 py-0.5 rounded" style={{ background: `${groupColor}18`, color: groupColor }}>
              {def.group}
            </span>
            <span className={`text-xs px-1.5 py-0.5 rounded border capitalize ${statusBg}`}>
              {node.status}
            </span>
          </div>
        </div>
        <div className="flex items-center gap-1 ml-2">
          <button
            onClick={() => onDeleteNode(node.id)}
            className="p-1.5 rounded-md text-muted-foreground hover:text-danger hover:bg-danger/10 transition-all"
            title="Delete node"
          >
            <Trash2 size={13} />
          </button>
          <button
            onClick={onClose}
            className="p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-secondary transition-all"
          >
            <X size={13} />
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-border">
        {tabs.map(tab => {
          const Icon = tab.icon;
          return (
            <button
              key={`inspector-tab-${tab.id}`}
              onClick={() => setActiveTab(tab.id)}
              className={`flex-1 flex items-center justify-center gap-1 py-2 text-xs font-medium transition-all ${
                activeTab === tab.id ? 'tab-active' : 'tab-inactive'
              }`}
            >
              <Icon size={11} />
              {tab.label}
            </button>
          );
        })}
      </div>

      {/* Tab content */}
      <div className="flex-1 overflow-y-auto scrollbar-thin p-4">
        {activeTab === 'overview' && (
          <div className="space-y-4">
            <p className="text-xs text-muted-foreground leading-relaxed">{def.description}</p>

            {/* Default specs */}
            <div className="space-y-2">
              <p className="text-xs font-semibold text-foreground">Default Specs</p>
              {[
                { label: 'Base Latency', value: `${def.defaultLatency}ms`, color: 'text-info' },
                { label: 'Max Throughput', value: `${def.defaultThroughput.toLocaleString()} RPS`, color: 'text-positive' },
                { label: 'Error Rate', value: `${def.defaultErrorRate}%`, color: def.defaultErrorRate > 1 ? 'text-warning' : 'text-positive' },
              ].map(row => (
                <div key={`spec-${row.label}`} className="flex items-center justify-between">
                  <span className="text-xs text-muted-foreground">{row.label}</span>
                  <span className={`text-xs font-semibold font-mono-nums ${row.color}`}>{row.value}</span>
                </div>
              ))}
            </div>

            {/* Live metrics */}
            {simState !== 'stopped' && (
              <div className="space-y-2">
                <p className="text-xs font-semibold text-foreground flex items-center gap-1">
                  <span className="w-1.5 h-1.5 rounded-full bg-positive animate-pulse inline-block" />
                  Live Metrics
                </p>
                {[
                  { label: 'RPS', value: `${node.metrics.rps.toLocaleString()}`, unit: 'req/s' },
                  { label: 'Latency', value: `${node.metrics.latency}`, unit: 'ms' },
                  { label: 'Error Rate', value: `${node.metrics.errorRate}`, unit: '%' },
                  { label: 'CPU Load', value: `${node.metrics.cpuLoad}`, unit: '%' },
                ].map(row => (
                  <div key={`live-${row.label}`} className="flex items-center justify-between">
                    <span className="text-xs text-muted-foreground">{row.label}</span>
                    <div className="flex items-center gap-1">
                      <span className="text-xs font-bold font-mono-nums text-foreground">{row.value}</span>
                      <span className="text-xs text-muted-foreground/60">{row.unit}</span>
                    </div>
                  </div>
                ))}
                {/* CPU bar */}
                <div>
                  <div className="h-1.5 bg-secondary rounded-full overflow-hidden">
                    <div
                      className={`h-full rounded-full transition-all duration-500 ${
                        node.metrics.cpuLoad > 85 ? 'bg-danger' :
                        node.metrics.cpuLoad > 65 ? 'bg-warning' : 'bg-positive'
                      }`}
                      style={{ width: `${node.metrics.cpuLoad}%` }}
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Node ID */}
            <div className="pt-2 border-t border-border">
              <p className="text-xs text-muted-foreground/60 font-mono">{node.id}</p>
            </div>
          </div>
        )}

        {activeTab === 'config' && (
          <div className="space-y-4">
            <p className="text-xs text-muted-foreground">Configure this component&apos;s behavior in the simulation.</p>

            {/* Per-client RPS — only for Client group nodes */}
            {def.group === 'Clients' && (
              <div>
                <label className="block text-xs font-medium text-foreground mb-1">
                  Request Rate (RPS)
                </label>
                <p className="text-xs text-muted-foreground mb-2">
                  Traffic this client generates per second. Summed across all connected clients as total input load.
                </p>
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    min={0}
                    max={100000}
                    step={100}
                    defaultValue={(node.config.clientRps as number) ?? 1000}
                    onChange={e => onUpdateConfig(node.id, { clientRps: Math.max(0, Number(e.target.value)) })}
                    className="flex-1 bg-secondary border border-border rounded-md px-3 py-1.5 text-xs text-foreground outline-none focus:ring-1 focus:ring-ring font-mono-nums"
                  />
                  <span className="text-xs text-muted-foreground shrink-0">req/s</span>
                </div>
                <div className="mt-2 flex items-center gap-1.5 text-xs text-muted-foreground/70">
                  <span className="w-1.5 h-1.5 rounded-full bg-accent inline-block shrink-0" />
                  <span>Requires an outgoing connection to inject traffic</span>
                </div>
              </div>
            )}

            {/* Scale */}
            <div>
              <label className="block text-xs font-medium text-foreground mb-1">Scale</label>
              <p className="text-xs text-muted-foreground mb-2">Horizontal scale factor (multiplies throughput capacity)</p>
              <div className="flex items-center gap-2">
                <input
                  type="range"
                  min={1}
                  max={20}
                  step={1}
                  defaultValue={(node.config.scale as number) ?? 1}
                  onChange={e => onUpdateConfig(node.id, { scale: Number(e.target.value) })}
                  className="flex-1 accent-primary"
                />
                <span className="text-xs font-semibold font-mono-nums text-foreground w-6 text-right">
                  {(node.config.scale as number) ?? 1}×
                </span>
              </div>
              <div className="flex justify-between text-xs text-muted-foreground mt-1 font-mono-nums">
                <span>1×</span>
                <span>20×</span>
              </div>
            </div>

            {/* Replicas */}
            <div>
              <label className="block text-xs font-medium text-foreground mb-1">Replicas</label>
              <p className="text-xs text-muted-foreground mb-2">Number of instances running in parallel</p>
              <input
                type="number"
                min={1}
                max={20}
                defaultValue={(node.config.replicas as number) ?? 1}
                onChange={e => onUpdateConfig(node.id, { replicas: Number(e.target.value) })}
                className="w-full bg-secondary border border-border rounded-md px-3 py-1.5 text-xs text-foreground outline-none focus:ring-1 focus:ring-ring font-mono-nums"
              />
            </div>

            {/* Latency */}
            <div>
              <label className="block text-xs font-medium text-foreground mb-1">Latency (ms)</label>
              <p className="text-xs text-muted-foreground mb-2">Override default response latency for this node</p>
              <input
                type="number"
                min={0}
                max={10000}
                defaultValue={(node.config.latencyOverride as number) ?? def.defaultLatency}
                onChange={e => onUpdateConfig(node.id, { latencyOverride: Number(e.target.value) })}
                className="w-full bg-secondary border border-border rounded-md px-3 py-1.5 text-xs text-foreground outline-none focus:ring-1 focus:ring-ring font-mono-nums"
              />
            </div>

            {/* Error Rate */}
            <div>
              <label className="block text-xs font-medium text-foreground mb-1">Error Rate (%)</label>
              <p className="text-xs text-muted-foreground mb-2">Simulated error injection rate for this node</p>
              <input
                type="range"
                min={0}
                max={100}
                step={0.5}
                defaultValue={(node.config.failureRate as number) ?? 0}
                onChange={e => onUpdateConfig(node.id, { failureRate: Number(e.target.value) })}
                className="w-full accent-primary"
              />
              <div className="flex justify-between text-xs text-muted-foreground mt-1 font-mono-nums">
                <span>0%</span>
                <span>{(node.config.failureRate as number ?? 0).toFixed(1)}%</span>
                <span>100%</span>
              </div>
            </div>

            {/* Cache Policy */}
            <div>
              <label className="block text-xs font-medium text-foreground mb-1">Cache Policy</label>
              <p className="text-xs text-muted-foreground mb-2">Caching strategy applied to this component</p>
              <select
                defaultValue={(node.config.cachePolicy as string) ?? 'none'}
                onChange={e => onUpdateConfig(node.id, { cachePolicy: e.target.value })}
                className="w-full bg-secondary border border-border rounded-md px-3 py-1.5 text-xs text-foreground outline-none focus:ring-1 focus:ring-ring cursor-pointer"
              >
                <option value="none">None — No caching</option>
                <option value="cache-aside">Cache-Aside — App manages cache</option>
                <option value="write-through">Write-Through — Sync write to cache + DB</option>
                <option value="write-back">Write-Back — Async write to DB</option>
                <option value="read-through">Read-Through — Cache fetches on miss</option>
                <option value="ttl-60">TTL 60s — Expire after 1 minute</option>
                <option value="ttl-300">TTL 300s — Expire after 5 minutes</option>
                <option value="lru">LRU — Evict least recently used</option>
              </select>
            </div>

            {/* Label */}
            <div>
              <label className="block text-xs font-medium text-foreground mb-1">Display Label</label>
              <input
                type="text"
                key={node.id}
                defaultValue={node.label}
                onChange={e => onUpdateLabel(node.id, e.target.value)}
                className="w-full bg-secondary border border-border rounded-md px-3 py-1.5 text-xs text-foreground outline-none focus:ring-1 focus:ring-ring"
              />
            </div>
          </div>
        )}

        {activeTab === 'learn' && (
          <div className="space-y-4">
            <div>
              <p className="text-xs font-semibold text-foreground mb-1.5 flex items-center gap-1.5">
                <BookOpen size={11} className="text-primary" />
                Concept
              </p>
              <p className="text-xs text-muted-foreground leading-relaxed">{learn.concept}</p>
            </div>
            <div>
              <p className="text-xs font-semibold text-foreground mb-1.5">When to Use</p>
              <p className="text-xs text-muted-foreground leading-relaxed">{learn.useCase}</p>
            </div>
            <div>
              <p className="text-xs font-semibold text-foreground mb-1.5">Trade-offs</p>
              <p className="text-xs text-muted-foreground leading-relaxed">{learn.tradeoffs}</p>
            </div>
            <div>
              <p className="text-xs font-semibold text-foreground mb-1.5">Common Pattern</p>
              <div className="bg-secondary rounded-md p-3 border border-border">
                <p className="text-xs text-muted-foreground leading-relaxed font-mono">{learn.pattern}</p>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'experiment' && (
          <div className="space-y-4">
            <p className="text-xs text-muted-foreground">Run focused experiments on this node to understand its behavior under different conditions.</p>

            <div className="space-y-3">
              {[
                { label: 'Latency Spike', desc: 'Inject 500ms latency for 10 seconds', icon: '⏱️', color: 'text-warning' },
                { label: 'Kill Node', desc: 'Simulate complete node failure', icon: '💀', color: 'text-danger' },
                { label: 'CPU Spike', desc: 'Pin CPU to 95% for 15 seconds', icon: '🔥', color: 'text-danger' },
                { label: 'Network Partition', desc: 'Drop 50% of packets to this node', icon: '✂️', color: 'text-warning' },
                { label: 'Memory Pressure', desc: 'Simulate OOM condition', icon: '💾', color: 'text-warning' },
                { label: 'Gradual Degradation', desc: 'Slowly increase error rate 0→100%', icon: '📉', color: 'text-info' },
              ].map(exp => (
                <button
                  key={`exp-${exp.label}`}
                  disabled={simState !== 'running'}
                  onClick={() => onTriggerExperiment(node.id, exp.label)}
                  className="w-full flex items-start gap-3 p-3 rounded-md bg-secondary hover:bg-secondary/80 border border-border hover:border-primary/30 transition-all text-left disabled:opacity-40 disabled:cursor-not-allowed group"
                >
                  <span className="text-base mt-0.5">{exp.icon}</span>
                  <div>
                    <p className={`text-xs font-semibold ${exp.color} group-hover:opacity-100`}>{exp.label}</p>
                    <p className="text-xs text-muted-foreground mt-0.5">{exp.desc}</p>
                  </div>
                </button>
              ))}
            </div>

            {simState !== 'running' && (
              <p className="text-xs text-muted-foreground/60 text-center pt-2">Start the simulation to run experiments</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}